import { timestampFormatter, TIMESTAMP_MINUTE_FORMAT } from "../../../core/utils/timestampFormatter";

/**
 * Format when an AI Assist action was actually sent/written.
 *
 * Empty/invalid values return "" so callers can omit the timestamp rather than
 * rendering "Invalid Date".
 *
 * @example
 * formatImplementedAtLabel("2026-08-19T11:22:00Z");
 * // "Aug 19, 2026 4:52 PM" (local timezone)
 *
 * @param {string|number|Date|null|undefined} timestamp
 * @returns {string}
 */
export const formatImplementedAtLabel = (timestamp) => {
  if (timestamp == null || timestamp === "") return "";
  const formatted = timestampFormatter(timestamp, TIMESTAMP_MINUTE_FORMAT);
  if (!formatted || formatted === "Invalid Date") return "";
  return formatted;
};

/**
 * Convert a Slack message `ts` (`"1700000001.000200"`) to an ISO string.
 *
 * Slack message ids are Unix seconds with a fractional suffix. Returns "" when
 * the value cannot be parsed, so it can be used as a fallback for `slack_sent_at`.
 *
 * @example
 * slackMessageTsToIso("1700000001.000200");
 * // "2023-11-14T22:13:21.000Z"
 *
 * @param {string|number|null|undefined} messageTs
 * @returns {string}
 */
export const slackMessageTsToIso = (messageTs) => {
  const n = Number(String(messageTs || "").trim());
  if (!Number.isFinite(n) || n <= 0) return "";
  return new Date(n * 1000).toISOString();
};

/**
 * Best available ISO timestamp for a sent Slack reply.
 *
 * Prefers a persisted `slack_sent_at`, then derives one from `slack_message_ts`.
 *
 * @example
 * slackSentAtFromResult({ slack_sent_at: "2026-08-19T11:22:00Z" });
 * // "2026-08-19T11:22:00Z"
 *
 * @param {object|null|undefined} result
 * @returns {string}
 */
export const slackSentAtFromResult = (result) => {
  const direct = String(result?.slack_sent_at || "").trim();
  if (direct) return direct;
  return slackMessageTsToIso(result?.slack_message_ts || result?.messageTs);
};
